import { IAction } from 'helpers/interfaces';
import { BrokenRoundsTypes } from '../../actions/configProvider/broken-rounds-actions';
import { Messages } from '../../../utils';
import i18next from 'i18next';

export type BrokenRoundsJobAction = 'RETRY' | 'RESOLVE' | 'ON_HOLD';

export interface IPendingBrokenRoundsBatch {
    action: BrokenRoundsJobAction;
    total: number;
}

export interface IPendingBrokenRoundsJob extends IPendingBrokenRoundsBatch {
    startedAt: number;
    registeredAtRequest: number;
}

export interface IBrokenRoundsData {
    data?: any;
    statusHistory?: any;
    sessionIds: number[] | null;
    roundUuids: string[] | null;
    totalNumberOfElements: number | null;
    isJobRunning: boolean;
    /** When job/status last reported RUNNING. */
    jobRunningConfirmedAt: number;
    jobFinishUntil: string | null;
    pendingBatch: IPendingBrokenRoundsBatch | null;
    pendingJob: IPendingBrokenRoundsJob | null;
    jobStatusSeq: number;
    jobStatusFailures: number;
    jobStatusRequestCount: number;
    jobStatusInFlight: boolean;
    jobStatusRequestedAt: number;
    completedJobsCount: number;
}

const JOB_FINISHED_MESSAGE_KEYS: Record<BrokenRoundsJobAction, string> = {
    RETRY: 'broken_rounds_retry_all_finished',
    RESOLVE: 'broken_rounds_resolve_all_finished',
    ON_HOLD: 'broken_rounds_on_hold_all_finished',
};

/**
 * A job cannot outlive round-manager's job TTL (900 s by default). While
 * job/status keeps failing, job state that has not been confirmed for longer
 * than that - our pending job and a RUNNING seen earlier - is dropped, so the
 * batch buttons do not stay disabled and polling falls back to the idle
 * cadence and its 30-minute cap.
 */
const JOB_STATE_MAX_AGE_MS = 16 * 60 * 1000;

/**
 * job/status requests time out after 10 s, so one that has not settled after
 * this long is not coming back and must not block polling on the next mount.
 */
const JOB_STATUS_IN_FLIGHT_MAX_AGE_MS = 15 * 1000;

export const brokenRoundsData: IBrokenRoundsData = {
    data: null,
    statusHistory: null,
    sessionIds: null,
    roundUuids: null,
    totalNumberOfElements: null,
    isJobRunning: false,
    jobRunningConfirmedAt: 0,
    jobFinishUntil: null,
    pendingBatch: null,
    pendingJob: null,
    jobStatusSeq: 0,
    jobStatusFailures: 0,
    jobStatusRequestCount: 0,
    jobStatusInFlight: false,
    jobStatusRequestedAt: 0,
    completedJobsCount: 0,
};

const startPendingBatch = (
    state: IBrokenRoundsData,
    action: BrokenRoundsJobAction,
    items: unknown[] | null | undefined,
): IBrokenRoundsData => ({
    ...state,
    pendingBatch: { action, total: items?.length ?? 0 },
});

const startPendingJob = (state: IBrokenRoundsData): IBrokenRoundsData => {
    if (!state.pendingBatch) {
        return state;
    }

    return {
        ...state,
        pendingBatch: null,
        pendingJob: {
            ...state.pendingBatch,
            startedAt: Date.now(),
            registeredAtRequest: state.jobStatusRequestCount,
        },
    };
};

const brokenRoundsReducer = (state = brokenRoundsData, actions: IAction) => {
    switch (actions.type) {
        case BrokenRoundsTypes.BROKEN_ROUNDS_REQUEST: {
            return {
                ...state,
                data: null,
                statusHistory: null,
                totalNumberOfElements: null,
                sessionIds: null,
                roundUuids: null,
            };
        }

        case BrokenRoundsTypes.BROKEN_ROUNDS_REFRESH_REQUEST: {
            // Background refresh: keep showing the current rows until new ones arrive.
            return state;
        }

        case BrokenRoundsTypes.BROKEN_ROUNDS_JOB_STATUS_REQUEST: {
            return {
                ...state,
                jobStatusRequestCount: state.jobStatusRequestCount + 1,
                jobStatusInFlight: true,
                jobStatusRequestedAt: Date.now(),
            };
        }

        case BrokenRoundsTypes.BROKEN_ROUNDS_JOB_STATUS_SUCCESS: {
            const isJobRunning = actions.payload?.status === 'RUNNING';
            const { pendingJob } = state;

            const ownJobFinished =
                !isJobRunning &&
                pendingJob !== null &&
                state.jobStatusRequestCount > pendingJob.registeredAtRequest;

            // A job started elsewhere (another operator, another tab) finished.
            // Refresh the grid, but do not report a count that is not ours.
            const foreignJobFinished = !isJobRunning && pendingJob === null && state.isJobRunning;

            if (pendingJob && ownJobFinished) {
                Messages.success(
                    i18next.t(JOB_FINISHED_MESSAGE_KEYS[pendingJob.action], {
                        total: pendingJob.total,
                    }),
                );
            }

            return {
                ...state,
                isJobRunning,
                jobRunningConfirmedAt: isJobRunning ? Date.now() : 0,
                jobFinishUntil: actions.payload?.finishUntil ?? null,
                pendingJob: ownJobFinished ? null : pendingJob,
                completedJobsCount:
                    ownJobFinished || foreignJobFinished
                        ? state.completedJobsCount + 1
                        : state.completedJobsCount,
                jobStatusSeq: state.jobStatusSeq + 1,
                jobStatusFailures: 0,
                jobStatusInFlight: false,
            };
        }

        case BrokenRoundsTypes.BROKEN_ROUNDS_JOB_STATUS_FAILED: {
            // A failed poll carries no information about the job, so it must never
            // be read as "finished" - but job state it cannot confirm expires.
            const now = Date.now();
            const isPendingJobExpired =
                state.pendingJob !== null &&
                now - state.pendingJob.startedAt > JOB_STATE_MAX_AGE_MS;
            const isRunningStateExpired =
                state.isJobRunning && now - state.jobRunningConfirmedAt > JOB_STATE_MAX_AGE_MS;

            return {
                ...state,
                isJobRunning: isRunningStateExpired ? false : state.isJobRunning,
                jobRunningConfirmedAt: isRunningStateExpired ? 0 : state.jobRunningConfirmedAt,
                pendingJob: isPendingJobExpired ? null : state.pendingJob,
                jobStatusSeq: state.jobStatusSeq + 1,
                jobStatusFailures: state.jobStatusFailures + 1,
                jobStatusInFlight: false,
            };
        }

        case BrokenRoundsTypes.BROKEN_ROUNDS_SUCCESS: {
            const { content, _metadata } = actions.payload;

            return {
                ...state,
                data: content,
                metadata: _metadata,
            };
        }

        case BrokenRoundsTypes.RETRY_SESSIONS_BATCH_REQUEST: {
            return startPendingBatch(state, 'RETRY', actions.payload?.roundUuids);
        }

        case BrokenRoundsTypes.UNBLOCK_SESSIONS_BATCH_REQUEST: {
            return startPendingBatch(state, 'RESOLVE', actions.payload?.sessionIds);
        }

        case BrokenRoundsTypes.ON_HOLD_BROKEN_ROUNDS_BATCH_REQUEST: {
            return startPendingBatch(state, 'ON_HOLD', actions.payload?.roundUuids);
        }

        case BrokenRoundsTypes.RETRY_SESSIONS_BATCH_SUCCESS:
        case BrokenRoundsTypes.UNBLOCK_SESSIONS_BATCH_SUCCESS:
        case BrokenRoundsTypes.ON_HOLD_BROKEN_ROUNDS_BATCH_SUCCESS: {
            return startPendingJob(state);
        }

        case BrokenRoundsTypes.RETRY_SESSIONS_BATCH_FAILED:
        case BrokenRoundsTypes.UNBLOCK_SESSIONS_BATCH_FAILED:
        case BrokenRoundsTypes.ON_HOLD_BROKEN_ROUNDS_BATCH_FAILED: {
            return { ...state, pendingBatch: null };
        }

        case BrokenRoundsTypes.GET_BROKEN_ROUNDS_COUNT_SUCCESS: {
            const { totalNumberOfElements, roundUuids, sessionIds } = actions.payload;

            return {
                ...state,
                totalNumberOfElements,
                roundUuids,
                sessionIds,
            };
        }

        case BrokenRoundsTypes.RESOLVE_SESSION_SUCCESS: {
            Messages.success(i18next.t('session_was_successfully_resolved'));

            return state;
        }

        case BrokenRoundsTypes.RESOLVE_SESSION_FAILED: {
            Messages.error(i18next.t('session_was_not_resolved'));

            return state;
        }

        case BrokenRoundsTypes.STATUS_HISTORY_SUCCESS: {
            const { statusHistoryRecords } = actions.payload;

            return {
                ...state,
                statusHistory: statusHistoryRecords,
            };
        }

        case BrokenRoundsTypes.RESET_BROKEN_ROUNDS_JOB_DATA: {
            // A job/status request may still be in flight when the page is left.
            // Keep the request bookkeeping so a page mounted before it settles does
            // not send a second, concurrent request.
            const isRequestStillInFlight =
                state.jobStatusInFlight &&
                Date.now() - state.jobStatusRequestedAt < JOB_STATUS_IN_FLIGHT_MAX_AGE_MS;

            return {
                ...brokenRoundsData,
                jobStatusSeq: state.jobStatusSeq,
                jobStatusRequestCount: state.jobStatusRequestCount,
                jobStatusInFlight: isRequestStillInFlight,
                jobStatusRequestedAt: state.jobStatusRequestedAt,
            };
        }

        default:
            return state;
    }
};

export default brokenRoundsReducer;
